/**
 * PendingCierreWhatsAppOverlay — Guardia anti-fraude global.
 *
 * Se renderiza en MainLayout. Si hay un cierre pendiente de envío al dueño
 * (persistido en localStorage), bloquea TODA la app con la previsualización
 * del ticket en MODO ENFORCE: sólo se libera cuando el empleado comparte
 * realmente la imagen con un destino concreto (WhatsApp, Web Share o
 * descarga del PNG).
 *
 * Reglas:
 *   - No tiene botón de cerrar / X / clic afuera / Escape: la única forma de
 *     salir es elegir destino y compartir.
 *   - El reporte en texto editable fue eliminado por riesgo de fraude.
 *   - Sobrevive a recargas, cambios de ruta y cierre involuntario del navegador.
 *   - El menú de compartir aparece abierto por defecto (1 sólo paso visible).
 */
import { useMemo } from "react";
import { usePersistentState } from "@/hooks";
import { txRead } from "@/lib/persistence/transactionalStore";
import type { CierreCaja } from "@/features/caja/types/caja.types";
import { CierreTicketPreview } from "@/features/caja/components/CierreTicketPreview";
import { defaultEmployees } from "@/mocks/data/employees";

export const PENDING_CIERRE_STORAGE_KEY = "caja.pendingWhatsAppReport";

export const PendingCierreWhatsAppOverlay = () => {
  const [cierre, setCierre] = usePersistentState<CierreCaja | null>(
    PENDING_CIERRE_STORAGE_KEY,
    null,
  );

  const empleadosCatalog = useMemo(() => {
    try {
      const parsed = txRead<typeof defaultEmployees | null>(
        "personal_empleados",
        null,
      );
      if (parsed && Array.isArray(parsed) && parsed.length > 0) {
        const ids = new Set(parsed.map((e) => e.id));
        const missing = defaultEmployees.filter((e) => !ids.has(e.id));
        return [...parsed, ...missing];
      }
    } catch {
      /* ignore */
    }
    return defaultEmployees;
  }, []);

  if (!cierre) return null;

  return (
    <CierreTicketPreview
      cierre={cierre}
      ventas={cierre.ventasSnapshot ?? []}
      empleadosCatalog={empleadosCatalog}
      enforceShare
      shareMenuHeader="Enviar cierre al dueño por"
      onClose={() => {
        // En modo enforceShare CierreTicketPreview no expone salida visible,
        // pero la prop es obligatoria. Nunca liberamos el bloqueo desde aquí:
        // sólo `onShared` (confirmación explícita del usuario) lo libera.
      }}
      onShared={() => setCierre(null)}
    />
  );
};

export default PendingCierreWhatsAppOverlay;
